import { useState } from 'react';
import { Loader } from 'lucide-react';
import Toast from '../common/Toast';

/**
 * ReviewActionDrawer
 * Right-side drawer for viewing and managing review actions (approve/reject)
 * 
 * Props:
 * - review: object - Review data to display
 * - isOpen: boolean - Whether drawer is visible
 * - onClose: () => void - Called when drawer should close
 * - onActionComplete: () => void - Called after successful action
 */
const ReviewActionDrawer = ({ review, isOpen, onClose, onActionComplete }) => {
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  if (!review) return null;

  // Check if action already taken - use status field (pending/approved/rejected)
  const isReadOnly = review.status !== 'pending';

  const handleApprove = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'https://vmc-backend-vt2w.onrender.com/api'}/business/reviews/${review.id}/approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        setToast({ type: 'error', message: data.message || 'Failed to approve review' });
        return;
      }

      setToast({ type: 'success', message: 'Review approved successfully' });
      setTimeout(() => {
        onActionComplete();
        onClose();
      }, 1000);
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to approve review' });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'https://vmc-backend-vt2w.onrender.com/api'}/business/reviews/${review.id}/reject`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          },
          body: JSON.stringify({
            rejection_reason: rejectionReason || null
          })
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        setToast({ type: 'error', message: data.message || 'Failed to reject review' });
        return;
      }

      setToast({ type: 'success', message: 'Review rejected successfully' });
      setRejectionReason('');
      setTimeout(() => {
        onActionComplete();
        onClose();
      }, 1000);
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to reject review' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = () => {
    if (review.status === 'approved') {
      return <span className="inline-flex rounded-full px-3 py-1 text-[11px] font-semibold bg-emerald-100 text-emerald-700">✅ Approved</span>;
    }
    if (review.status === 'rejected') {
      return <span className="inline-flex rounded-full px-3 py-1 text-[11px] font-semibold bg-red-100 text-red-700">❌ Rejected</span>;
    }
    return <span className="inline-flex rounded-full px-3 py-1 text-[11px] font-semibold bg-amber-100 text-amber-700">⏳ Pending</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '–';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-30 transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed right-0 top-0 bottom-0 w-full max-w-[550px] bg-white shadow-xl z-40 transform transition-all duration-300 flex flex-col
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* ── Header ── */}
        <div className="flex-shrink-0 border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[15px] font-bold text-slate-900">Review Details</h2>
            <button
              onClick={onClose}
              disabled={loading}
              className="p-1 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-[13px] font-semibold text-slate-900">{review.title}</p>
              <p className="text-[11px] text-slate-500 mt-1">By {review.reviewer_name}</p>
            </div>
          </div>
        </div>

        {/* ── Content (Scrollable) ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Rating */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-2">Rating</label>
            <div className="flex items-center gap-3">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(i => (
                  <svg key={i} width="20" height="20" viewBox="0 0 24 24"
                    fill={i <= review.rating ? '#f6ce3b' : 'none'}
                    stroke="#f6ce3b" strokeWidth="1.5">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                ))}
              </div>
              <span className="text-[12px] font-semibold text-slate-900">{review.rating}/5</span>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-2">Status</label>
            {getStatusBadge()}
          </div>

          {/* Reviewer Info */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Reviewer Email</span>
              <span className="text-[12px] text-slate-900 font-medium">{review.reviewer_email || '–'}</span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-slate-200">
              <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Posted</span>
              <span className="text-[12px] text-slate-900 font-medium">{formatDate(review.created_at)}</span>
            </div>
          </div>

          {/* Review Content */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-2">Review</label>
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <p className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap">{review.content || '–'}</p>
            </div>
          </div>

          {/* Experience Date */}
          {review.experience_date && (
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-2">Experience Date</label>
              <p className="text-[12px] text-slate-900 font-medium">{formatDate(review.experience_date)}</p>
            </div>
          )}

          {/* Approval Timeline */}
          {(review.reviewed_at || review.rejection_reason) && (
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-3">Timeline</label>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-2 h-2 mt-1 rounded-full bg-slate-300"></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium text-slate-900">Posted</p>
                    <p className="text-[11px] text-slate-500">{formatDate(review.created_at)}</p>
                  </div>
                </div>
                {review.reviewed_at && (
                  <div className="flex gap-3">
                    <div className={`flex-shrink-0 w-2 h-2 mt-1 rounded-full ${review.status === 'approved' ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-medium text-slate-900">{review.status === 'approved' ? 'Approved' : 'Rejected'}</p>
                      <p className="text-[11px] text-slate-500">{formatDate(review.reviewed_at)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rejection Reason - Show if rejected */}
          {review.status === 'rejected' && review.rejection_reason && (
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-2">Rejection Reason</label>
              <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                <p className="text-[12px] text-red-700">{review.rejection_reason}</p>
              </div>
            </div>
          )}

          {/* Rejection Reason Input - Show only when rejecting */}
          {!isReadOnly && (
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-2">Rejection Reason (Optional)</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this review cannot be published..."
                maxLength="500"
                rows="3"
                className="w-full px-3 py-2 text-[12px] border border-slate-200 rounded-lg focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-100 resize-none"
              />
              <p className="text-[10px] text-slate-400 mt-1">{rejectionReason.length}/500</p>
            </div>
          )}
        </div>

        {/* ── Footer (Sticky) ── */}
        {!isReadOnly ? (
          <div className="flex-shrink-0 border-t border-slate-200 px-6 py-4 bg-slate-50 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 text-[12px] font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleReject}
              disabled={loading}
              className="flex-1 px-4 py-2 text-[12px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              title={rejectionReason ? 'With reason: ' + rejectionReason : 'Reject without reason'}
            >
              {loading && <Loader size={14} className="animate-spin" />}
              Reject {rejectionReason && '✓'}
            </button>
            <button
              type="button"
              onClick={handleApprove}
              disabled={loading}
              className="flex-1 px-4 py-2 text-[12px] font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader size={14} className="animate-spin" />}
              Approve
            </button>
          </div>
        ) : (
          <div className="flex-shrink-0 border-t border-slate-200 px-6 py-4 bg-blue-50">
            <div className="flex items-center gap-2 text-blue-700 text-[12px]">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              Action already taken on this review (Read-Only)
            </div>
          </div>
        )}
      </div>

      {/* Toast Notification */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default ReviewActionDrawer;
